import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const downloadFile = async (url) => {
  const response = await api.get(url, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'];

  let filename = 'download';

  if (disposition) {
    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (encodedMatch?.[1]) {
      filename = decodeURIComponent(encodedMatch[1]);
    } else if (plainMatch?.[1]) {
      filename = plainMatch[1];
    }
  }

  const blob = new Blob([response.data]);
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};


// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  register: async (email, fullName, password) => {
    const response = await api.post('/auth/register', {
      email,
      full_name: fullName,
      password,
    });
    return response.data;
  },

  verifyRegistration: async (userId, code) => {
    const response = await api.post('/auth/verify-registration', {
      user_id: userId,
      code: code,
    });
    return response.data;
  },

  login: async (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  resendCode: async (userId) => {
    const response = await api.post(`/auth/resend-code?user_id=${userId}`);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateCurrentUser: async (userData) => {
    const response = await api.put('/auth/me', userData);
    return response.data;
  },

  getColleagues: async () => {
    const response = await api.get('/auth/colleagues');
    return response.data;
  },

  getDepartments: async () => {
    const response = await api.get('/auth/departments');
    return response.data;
  },

  getRoles: async () => {
    const response = await api.get('/auth/roles');
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
    }
  },
};

export const adminService = {
  getAuthSessions: async ({ userId, limit = 100, offset = 0 } = {}) => {
    const response = await api.get('/admin/audit/auth-sessions', {
      params: { user_id: userId, limit, offset },
    });
    return response.data;
  },

  // User management
  getAllUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  // Role management
  getAllRoles: async () => {
    const response = await api.get('/admin/roles');
    return response.data;
  },

  updateUserRoles: async (userId, roleNames) => {
    const response = await api.put(`/admin/users/${userId}/roles`, {
      role_names: roleNames,
    });
    return response.data;
  },

  addRoleToUser: async (userId, roleName) => {
    const response = await api.post(`/admin/users/${userId}/roles/${roleName}`);
    return response.data;
  },

  removeRoleFromUser: async (userId, roleName) => {
    const response = await api.delete(`/admin/users/${userId}/roles/${roleName}`);
    return response.data;
  },

  // Permission management
  getAllPermissions: async () => {
    const response = await api.get('/admin/permissions');
    return response.data;
  },

  getPermissionsByCategory: async (category) => {
    const response = await api.get(`/admin/permissions/category/${category}`);
    return response.data;
  },

  // User permission management
  updateUserPermissions: async (userId, permissionNames) => {
    const response = await api.put(`/admin/users/${userId}/permissions`, {
      permission_names: permissionNames,
    });
    return response.data;
  },

  addPermissionToUser: async (userId, permissionName) => {
    const response = await api.post(`/admin/users/${userId}/permissions/${permissionName}`);
    return response.data;
  },

  removePermissionFromUser: async (userId, permissionName) => {
    const response = await api.delete(`/admin/users/${userId}/permissions/${permissionName}`);
    return response.data;
  },

  // Role permission management
  updateRolePermissions: async (roleName, permissionNames) => {
    const response = await api.put(`/admin/roles/${roleName}/permissions`, {
      permission_names: permissionNames,
    });
    return response.data;
  },

  addPermissionToRole: async (roleName, permissionName) => {
    const response = await api.post(`/admin/roles/${roleName}/permissions/${permissionName}`);
    return response.data;
  },

  removePermissionFromRole: async (roleName, permissionName) => {
    const response = await api.delete(`/admin/roles/${roleName}/permissions/${permissionName}`);
    return response.data;
  },

  // Seeding
  seedPermissions: async () => {
    const response = await api.post('/admin/seed-permissions');
    return response.data;
  },
};

// Content management service
export const contentService = {
  // Public endpoints
  getHomePageData: async () => {
    const response = await api.get('/content/home');
    return response.data;
  },

  getPublicNews: async (limit = 10) => {
    const response = await api.get(`/content/news?limit=${limit}`);
    return response.data;
  },

  getPublicAnnouncements: async () => {
    const response = await api.get('/content/announcements');
    return response.data;
  },

  getPriorities: async () => {
    const response = await api.get('/content/priorities');
    return response.data;
  },

  // Admin news management
  getAllNews: async (includeUnpublished = true) => {
    const response = await api.get(`/content/admin/news?include_unpublished=${includeUnpublished}`);
    return response.data;
  },

  getNewsById: async (newsId) => {
    const response = await api.get(`/content/admin/news/${newsId}`);
    return response.data;
  },

  createNews: async (newsData) => {
    const response = await api.post('/content/admin/news', newsData);
    return response.data;
  },

  updateNews: async (newsId, newsData) => {
    const response = await api.put(`/content/admin/news/${newsId}`, newsData);
    return response.data;
  },

  deleteNews: async (newsId) => {
    const response = await api.delete(`/content/admin/news/${newsId}`);
    return response.data;
  },

  // Admin announcement management
  getAllAnnouncements: async (includeUnpublished = true, includeExpired = false) => {
    const response = await api.get(`/content/admin/announcements?include_unpublished=${includeUnpublished}&include_expired=${includeExpired}`);
    return response.data;
  },

  // Get all announcements grouped by department (admin only)
  getAllAnnouncementsByDepartment: async (includeExpired = false) => {
    const response = await api.get(`/content/admin/all-announcements?include_expired=${includeExpired}`);
    return response.data;
  },

  getAnnouncementById: async (announcementId) => {
    const response = await api.get(`/content/admin/announcements/${announcementId}`);
    return response.data;
  },

  createAnnouncement: async (announcementData) => {
    const response = await api.post('/content/admin/announcements', announcementData);
    return response.data;
  },

  updateAnnouncement: async (announcementId, announcementData) => {
    const response = await api.put(`/content/admin/announcements/${announcementId}`, announcementData);
    return response.data;
  },

  deleteAnnouncement: async (announcementId) => {
    const response = await api.delete(`/content/admin/announcements/${announcementId}`);
    return response.data;
  },
};

// Trainings service
export const trainingsService = {
  // Public endpoints
  getAllTrainings: async () => {
    const response = await api.get('/trainings');
    return response.data;
  },

  getTrainingById: async (trainingId) => {
    const response = await api.get(`/trainings/${trainingId}`);
    return response.data;
  },

  // Admin endpoints
  getAllTrainingsAdmin: async () => {
    const response = await api.get('/trainings/admin/all');
    return response.data;
  },

  createTraining: async (trainingData) => {
    const response = await api.post('/trainings', trainingData);
    return response.data;
  },

  updateTraining: async (trainingId, trainingData) => {
    const response = await api.put(`/trainings/${trainingId}`, trainingData);
    return response.data;
  },

  deleteTraining: async (trainingId) => {
    const response = await api.delete(`/trainings/${trainingId}`);
    return response.data;
  },

  cancelTraining: async (trainingId) => {
    const response = await api.post(`/trainings/${trainingId}/cancel`);
    return response.data;
  },

  // Logo management
  getLogos: async () => {
    const response = await api.get('/trainings/logos/list');
    return response.data;
  },

  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/trainings/logos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteLogo: async (filename) => {
    const response = await api.delete(`/trainings/logos/${filename}`);
    return response.data;
  },

  getLogoUrl: (filename) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}/trainings/logos/${filename}`;
  },

  // Attachment management
  getAttachments: async () => {
    const response = await api.get('/trainings/attachments/list');
    return response.data;
  },

  uploadAttachment: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/trainings/attachments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteAttachment: async (filename) => {
    const response = await api.delete(`/trainings/attachments/${filename}`);
    return response.data;
  },

  getAttachmentUrl: (filename) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}/trainings/attachments/${filename}`;
  },
};

// Consultations/Meetings service
export const consultationsService = {
  getAll: async () => {
    const response = await api.get('/consultations');
    return response.data;
  },

  updateStatus: async (id, statusData) => {
    const response = await api.patch(`/consultations/${id}`, statusData);
    return response.data;
  },

  assign: async (id, assignedToId) => {
    const response = await api.post(`/consultations/${id}/assign`, { assigned_to_id: assignedToId });
    return response.data;
  },

  getMyAssigned: async () => {
    const response = await api.get('/consultations/my-assigned');
    return response.data;
  },

  getAssignableEmployees: async () => {
    const response = await api.get('/consultations/assignable-employees');
    return response.data;
  },

  getAssignmentHistory: async () => {
    const response = await api.get('/consultations/assignment-history');
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/consultations/${id}`);
    return response.data;
  },

  getStatistics: async () => {
    const response = await api.get('/consultations/statistics');
    return response.data;
  },

  getEmployeeConsultations: async (employeeId) => {
    const response = await api.get(`/consultations/employee/${employeeId}`);
    return response.data;
  },

  downloadAttachment: async (consultationId, attachmentId, filename) => {
    try {
      const response = await api.get(`/consultations/${consultationId}/attachments/${attachmentId}`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  },

  getAttachmentUrl: (consultationId, attachmentId) => {
    return `${API_URL}/consultations/${consultationId}/attachments/${attachmentId}`;
  },
};

// Order Tracking service
export const orderTrackingService = {
  syncEmails: async () => {
    const response = await api.post('/order-tracking/sync-emails');
    return response.data;
  },

  rebuildEmails: async () => {
    const response = await api.post('/order-tracking/rebuild-emails');
    return response.data;
  },

  getLastSync: async () => {
    const response = await api.get('/order-tracking/last-sync');
    return response.data;
  },

  getPendingMaterials: async (department, deliveryAddress) => {
    const response = await api.get(`/order-tracking/pending-materials/${department}`, {
      params: deliveryAddress ? { delivery_address: deliveryAddress } : {},
    });
    return response.data;
  },

  getOrderDeliveryStatus: async (department, identifier, deliveryAddress) => {
    const response = await api.post(`/order-tracking/orders/status/${department}`, {
      identifier,
    }, {
      params: deliveryAddress ? { delivery_address: deliveryAddress } : {},
    });
    return response.data;
  },

  getOrderIdentifierSuggestions: async (department, query, deliveryAddress) => {
    const response = await api.get(`/order-tracking/orders/suggestions/${department}`, {
      params: {
        q: query,
        limit: 10,
        ...(deliveryAddress ? { delivery_address: deliveryAddress } : {}),
      },
    });
    return response.data;
  },

  getPossibleDuplicateOrders: async (department, deliveryAddress) => {
    const response = await api.get(`/order-tracking/orders/possible-duplicates/${department}`, {
      params: {
        lookback: 10,
        ...(deliveryAddress ? { delivery_address: deliveryAddress } : {}),
      },
    });
    return response.data;
  },

  getMaterialsOldNew: async (department, deliveryAddress) => {
    const response = await api.get(`/order-tracking/materials-old-new/${department}`, {
      params: deliveryAddress ? { delivery_address: deliveryAddress } : {},
    });
    return response.data;
  },

  getPredictions: async (department, deliveryAddress) => {
    const response = await api.get(`/order-tracking/predictions/${department}`, {
      params: deliveryAddress ? { delivery_address: deliveryAddress } : {},
    });
    return response.data;
  },

  getMaterialPrediction: async (department, materialNumber, deliveryAddress) => {
    const response = await api.post(`/order-tracking/predictions/material/${department}`, {
      material_number: materialNumber,
    }, {
      params: deliveryAddress ? { delivery_address: deliveryAddress } : {},
    });
    return response.data;
  },

  // ─── EXPORTS ─────────────────────

  exportPendingMaterials: (department, format = 'csv', deliveryAddress) =>
    downloadFile(`/order-tracking/pending-materials/${department}/export?format=${format}${deliveryAddress ? `&delivery_address=${encodeURIComponent(deliveryAddress)}` : ''}`),

  exportMaterialsOldNew: (department, format = 'csv', deliveryAddress) =>
    downloadFile(`/order-tracking/materials-old-new/${department}/export?format=${format}${deliveryAddress ? `&delivery_address=${encodeURIComponent(deliveryAddress)}` : ''}`),

  exportPredictions: (department, format = 'csv', deliveryAddress) =>
    downloadFile(`/order-tracking/predictions/${department}/export?format=${format}${deliveryAddress ? `&delivery_address=${encodeURIComponent(deliveryAddress)}` : ''}`),
};

export const orderAnalyticsService = {
  getWinkhausAnalytics: async ({ startDate, endDate, department } = {}) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (department) params.department = department;
    const response = await api.get('/order-tracking/analytics/winkhaus', { params });
    return response.data;
  },
  getSchucoAnalytics: async ({ startDate, endDate } = {}) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/order-tracking/analytics/schuco', { params });
    return response.data;
  },
};

export const toolAnalyticsService = {
  getWeeklyUsage: async (year = 2026) => {
    const response = await api.get('/tool-analytics/weekly', { params: { year } });
    return response.data;
  },
};

// Schüco Order Tracking service
export const schucoTrackingService = {
  syncEmails: async () => {
    const response = await api.post('/order-tracking/schuco/sync-emails');
    return response.data;
  },

  rebuildEmails: async () => {
    const response = await api.post('/order-tracking/schuco/rebuild-emails');
    return response.data;
  },

  getLastSync: async () => {
    const response = await api.get('/order-tracking/schuco/last-sync');
    return response.data;
  },

  getPendingItems: async () => {
    const response = await api.get('/order-tracking/schuco/pending-items');
    return response.data;
  },

  getComplaintItems: async () => {
    const response = await api.get('/order-tracking/schuco/complaints');
    return response.data;
  },

  getComplaintCount: async () => {
    const response = await api.get('/order-tracking/schuco/complaints/count');
    return response.data.count;
  },

  markComplaintItemReceived: async ({ order_number, position, item_number, delivery_date }) => {
    const response = await api.post('/order-tracking/schuco/complaints/mark-received', {
      order_number,
      position,
      item_number,
      delivery_date,
    });
    return response.data;
  },

  getOrderDeliveryStatus: async (identifier) => {
    const response = await api.post('/order-tracking/schuco/orders/status', {
      identifier,
    });
    return response.data;
  },

  getOrderIdentifierSuggestions: async (query) => {
    const response = await api.get('/order-tracking/schuco/orders/suggestions', {
      params: { q: query, limit: 10 },
    });
    return response.data;
  },

  getPossibleDuplicateOrders: async () => {
    const response = await api.get('/order-tracking/schuco/orders/possible-duplicates', {
      params: { lookback: 10 },
    });
    return response.data;
  },

  getPredictions: async () => {
    const response = await api.get('/order-tracking/schuco/predictions');
    return response.data;
  },

  getItemPrediction: async (itemNumber) => {
    const response = await api.post('/order-tracking/schuco/predictions/item', {
      item_number: itemNumber,
    });
    return response.data;
  },

  savePredictionCorrection: async (correction) => {
    const response = await api.put('/order-tracking/schuco/predictions/correction', correction);
    return response.data;
  },

  deletePredictionCorrection: async (itemNumber) => {
    await api.delete(`/order-tracking/schuco/predictions/correction/${encodeURIComponent(itemNumber)}`);
  },

  // ─── EXPORTS ─────────────────────

  exportPendingItems: (format = 'csv') =>
    downloadFile(`/order-tracking/schuco/pending-items/export?format=${format}`, { responseType: 'blob' }),

  exportPredictions: (format = 'csv') =>
    downloadFile(`/order-tracking/schuco/predictions/export?format=${format}`, { responseType: 'blob' }),
};

// Blinds delivery predictions
export const blindsTrackingService = {
  getPredictions: async () => {
    const response = await api.get('/order-tracking/blinds/predictions');
    return response.data;
  },

  savePredictionCorrection: async (correction) => {
    const response = await api.put('/order-tracking/blinds/predictions/correction', correction);
    return response.data;
  },

  deletePredictionCorrection: async (itemNumber) => {
    await api.delete(`/order-tracking/blinds/predictions/correction/${encodeURIComponent(itemNumber)}`);
  },
};

// Glass Order Tracking service (PressGlass / AGC Glass)
export const glassTrackingService = {
  syncEmails: async () => {
    const response = await api.post('/order-tracking/glass/sync-emails');
    return response.data;
  },

  getPendingItems: async () => {
    const response = await api.get('/order-tracking/glass/pending-items');
    return response.data;
  },
};

// Notifications service
export const notificationService = {
  getNotifications: async (limit = 50, unreadOnly = false) => {
    const response = await api.get(`/notifications?limit=${limit}&unread_only=${unreadOnly}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (notificationIds) => {
    const response = await api.post('/notifications/mark-read', {
      notification_ids: notificationIds,
    });
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.post('/notifications/mark-all-read');
    return response.data;
  },

  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  deleteAllNotifications: async () => {
    await api.delete('/notifications/all');
  },
};

// Reviews Analytics service
const buildReviewParams = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.startDate) params.append('start_date', filters.startDate);
  if (filters.endDate) params.append('end_date', filters.endDate);
  if (filters.department && filters.department !== 'all') params.append('department', filters.department);
  if (filters.sentiment && filters.sentiment !== 'all') params.append('sentiment', filters.sentiment);
  if (filters.minRating) params.append('min_rating', filters.minRating);
  if (filters.maxRating) params.append('max_rating', filters.maxRating);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.topic) params.append('topic', filters.topic);
  if (filters.theme) {
    params.append('theme', filters.theme);
    if (filters.themeSide) params.append('theme_side', filters.themeSide);
  }
  if (filters.strength) params.append('strength', filters.strength);
  if (filters.weakness) params.append('weakness', filters.weakness);
  if (filters.product) params.append('product', filters.product);
  if (filters.urgency) params.append('urgency', filters.urgency);
  if (filters.hasText === true || filters.hasText === false) params.append('has_text', filters.hasText);
  if (filters.hasReply === true || filters.hasReply === false) params.append('has_reply', filters.hasReply);
  if (filters.search) params.append('search', filters.search);
  return params;
};

export const reviewsService = {
  getAnalytics: async (filters = {}, options = {}) => {
    const params = buildReviewParams(filters);
    if (options.compare) params.append('compare', options.compare);
    if (options.granularity) params.append('granularity', options.granularity);
    if (options.includeNegativeDetails === false) params.append('include_negative_details', 'false');

    const response = await api.get(`/reviews/analytics?${params}`, { signal: options.signal });
    return response.data;
  },

  getReviews: async (filters = {}, { sort = 'newest', page = 1, pageSize = 20, signal } = {}) => {
    const params = buildReviewParams(filters);
    params.append('sort', sort);
    params.append('page', page);
    params.append('page_size', pageSize);

    const response = await api.get(`/reviews/list?${params}`, { signal });
    return response.data;
  },

  getFilterOptions: async () => {
    const response = await api.get('/reviews/filters');
    return response.data;
  },

  getYears: async () => {
    const response = await api.get('/reviews/years');
    return response.data;
  },

  getDepartments: async () => {
    const response = await api.get('/reviews/departments');
    return response.data;
  },
};

// ─────────────────────────────────────────────
//  Installation Scheduling service
// ─────────────────────────────────────────────
export const installationService = {
  // Calendar
  getCalendar: async (startDate, endDate) => {
    const response = await api.get('/installation/calendar', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  // Installations
  listInstallations: async (state, search) => {
    const response = await api.get('/installation/installations', {
      params: { state, search },
    });
    return response.data;
  },

  // One page of the installation list. `total` comes from X-Total-Count
  // (null when the header is not readable, e.g. not exposed by CORS).
  listInstallationsPage: async ({
    state, search, pzl, calendarStatuses, limit, offset,
  } = {}) => {
    const response = await api.get('/installation/installations', {
      params: {
        state,
        search,
        pzl,
        limit,
        offset,
        calendar_statuses: calendarStatuses?.length ? calendarStatuses.join(',') : undefined,
      },
    });
    const total = Number.parseInt(response.headers?.['x-total-count'], 10);
    return { items: response.data, total: Number.isFinite(total) ? total : null };
  },

  listUnscheduled: async () => (
    await api.get('/installation/installations', { params: { unscheduled: true } })
  ).data,

  scheduleInstallation: async (id, data) => (
    await api.post(`/installation/installations/${id}/schedule`, data)
  ).data,

  getInstallation: async (id) => {
    const response = await api.get(`/installation/installations/${id}`);
    return response.data;
  },

  createInstallation: async (data) => {
    const response = await api.post('/installation/installations', data);
    return response.data;
  },

  quickAddInstallation: async (data) => (
    await api.post('/installation/installations/quick-add', data)
  ).data,

  createCalendarNote: async (data) => (
    await api.post('/installation/installations/calendar-note', data)
  ).data,

  // Atomowo tworzy zlecenie powiązane ("-PR" premium / "-DP" dodatkowa praca)
  // razem z pozycją, datami i ekipą – błąd nie zostawia częściowo utworzonego zlecenia.
  createRelatedInstallation: async (id, data) => (
    await api.post(`/installation/installations/${id}/related-order`, data)
  ).data,

  updateInstallation: async (id, data) => {
    const response = await api.put(`/installation/installations/${id}`, data);
    return response.data;
  },

  updateInstallationCalendar: async (id, data) => {
    const response = await api.patch(`/installation/installations/${id}/calendar`, data);
    return response.data;
  },

  deleteInstallation: async (id, scope = 'single', occurrence = null) => {
    await api.delete(`/installation/installations/${id}`, { params: {
      scope,
      assigned_date: occurrence?.assignedDate,
      ...(Number.isInteger(occurrence?.teamId) ? { team_id: occurrence.teamId } : {}),
    } });
  },

  importFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/installation/installations/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importPlannerFiles: async (plannerFile, positionsFile) => {
    const formData = new FormData();
    formData.append('planner_file', plannerFile);
    formData.append('positions_file', positionsFile);
    const response = await api.post('/installation/installations/import-planner', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importReceivedPositions: async (positionsFile) => {
    const formData = new FormData();
    formData.append('positions_file', positionsFile);
    const response = await api.post('/installation/installations/import-received', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDistinctStates: async () => {
    const response = await api.get('/installation/states');
    return response.data;
  },

  // Installation Items
  createInstallationItem: async (installationId, data) => {
    const response = await api.post(`/installation/installations/${installationId}/items`, data);
    return response.data;
  },

  updateInstallationItem: async (itemId, data) => {
    const response = await api.put(`/installation/items/${itemId}`, data);
    return response.data;
  },

  deleteInstallationItem: async (itemId) => {
    await api.delete(`/installation/items/${itemId}`);
  },

  // Assignments
  createAssignment: async (installationId, data) => {
    const response = await api.post(`/installation/installations/${installationId}/assignments`, data);
    return response.data;
  },

  deleteAssignment: async (assignmentId) => {
    await api.delete(`/installation/assignments/${assignmentId}`);
  },

  moveAssignmentToTeam: async (assignmentId, teamId) => {
    const response = await api.put(`/installation/assignments/${assignmentId}/team`, {
      team_id: teamId,
    });
    return response.data;
  },

  reorderAssignments: async (assignmentIds) => {
    const response = await api.put('/installation/assignments/reorder', {
      assignment_ids: assignmentIds,
    });
    return response.data;
  },

  getTeamDayComments: async (startDate, endDate) => {
    const response = await api.get('/installation/team-day-comments', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  getCalendarVacations: async (startDate, endDate) => {
    const response = await api.get('/installation/vacations/calendar', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  saveTeamDayComment: async (teamId, commentDate, content) => {
    const response = await api.put(`/installation/teams/${teamId}/day-comments/${commentDate}`, {
      content,
    });
    return response.data;
  },

  saveTeamDayComments: async (teamIds, commentDates, content, sourceTeamId, sourceCommentDate) => {
    const response = await api.put('/installation/team-day-comments', {
      team_ids: Array.isArray(teamIds) ? teamIds : [teamIds],
      comment_dates: commentDates,
      content,
      source_team_id: sourceTeamId,
      source_comment_date: sourceCommentDate,
    });
    return response.data;
  },

  promoteTeamDayCommentToCalendarNote: async (teamId, commentDate, content, scope = 'single') => {
    const response = await api.post(
      `/installation/teams/${teamId}/day-comments/${commentDate}/calendar-note`,
      { content, scope }
    );
    return response.data;
  },

  deleteTeamDayComment: async (teamId, commentDate, scope = 'single') => {
    await api.delete(`/installation/teams/${teamId}/day-comments/${commentDate}`, {
      params: { scope },
    });
  },

  getWeeklyHiddenTeams: async (weekStart) => {
    const response = await api.get('/installation/weekly-hidden-teams', {
      params: { week_start: weekStart },
    });
    return response.data;
  },

  saveWeeklyHiddenTeams: async (weekStart, teamIds) => {
    const response = await api.put('/installation/weekly-hidden-teams', {
      team_ids: teamIds,
    }, {
      params: { week_start: weekStart },
    });
    return response.data;
  },

  // Installers
  listInstallers: async (activeOnly = false) => {
    const response = await api.get('/installation/installers', {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  listInstallerVacationRates: async () => {
    const response = await api.get('/installation/installers/vacation-rates');
    return response.data;
  },

  createInstaller: async (data) => {
    const response = await api.post('/installation/installers', data);
    return response.data;
  },

  updateInstaller: async (id, data) => {
    const response = await api.put(`/installation/installers/${id}`, data);
    return response.data;
  },

  updateInstallerVacationRates: async (id, vacationRates) => {
    const response = await api.put(`/installation/installers/${id}/vacation-rates`, {
      vacation_rates: vacationRates,
    });
    return response.data;
  },

  deleteInstaller: async (id) => {
    await api.delete(`/installation/installers/${id}`);
  },

  // Teams
  listTeams: async () => {
    const response = await api.get('/installation/teams');
    return response.data;
  },

  createTeam: async (data) => {
    const response = await api.post('/installation/teams', data);
    return response.data;
  },

  updateTeam: async (id, data) => {
    const response = await api.put(`/installation/teams/${id}`, data);
    return response.data;
  },

  deleteTeam: async (id) => {
    await api.delete(`/installation/teams/${id}`);
  },

  addTeamMember: async (teamId, installerId) => {
    const response = await api.post(`/installation/teams/${teamId}/members/${installerId}`);
    return response.data;
  },

  removeTeamMember: async (teamId, installerId) => {
    const response = await api.delete(`/installation/teams/${teamId}/members/${installerId}`);
    return response.data;
  },

  // Positions
  listPositions: async () => {
    const response = await api.get('/installation/positions');
    return response.data;
  },

  createPosition: async (data) => {
    const response = await api.post('/installation/positions', data);
    return response.data;
  },

  updatePosition: async (id, data) => {
    const response = await api.put(`/installation/positions/${id}`, data);
    return response.data;
  },

  deletePosition: async (id) => {
    await api.delete(`/installation/positions/${id}`);
  },

  reorderPositions: async (positions) => {
    const response = await api.post('/installation/positions/reorder', { positions });
    return response.data;
  },

  // Vacations
  listVacations: async (installerId) => {
    const response = await api.get(`/installation/installers/${installerId}/vacations`);
    return response.data;
  },

  getUpcomingVacations: async () => {
    const response = await api.get('/installation/vacations/upcoming');
    return response.data;
  },

  getPendingVacations: async () => {
    const response = await api.get('/installation/vacations/pending');
    return response.data;
  },

  getAllVacations: async () => {
    const response = await api.get('/installation/vacations/all');
    return response.data;
  },

  exportVacations: async (format, month) => (
    downloadFile(
      `/installation/vacations/export/${format}`
      + `?month=${encodeURIComponent(month)}`
    )
  ),

  createVacation: async (installerId, data) => {
    const response = await api.post(`/installation/installers/${installerId}/vacations`, data);
    return response.data;
  },

  updateVacation: async (vacationId, data) => {
    const response = await api.put(`/installation/vacations/${vacationId}`, data);
    return response.data;
  },

  deleteVacation: async (vacationId) => {
    await api.delete(`/installation/vacations/${vacationId}`);
  },

  // Settlement
  getInstallationSettlement: async (installationId, overheadPercentage, distributionPercentage) => {
    const params = {};
    if (overheadPercentage !== undefined && overheadPercentage !== null && overheadPercentage !== '') {
      params.overhead_percentage = overheadPercentage;
    }
    if (distributionPercentage !== undefined && distributionPercentage !== null && distributionPercentage !== '') {
      params.distribution_percentage = distributionPercentage;
    }
    const response = await api.get(`/installation/installations/${installationId}/settlement`, { params });
    return response.data;
  },
};

// ─────────────────────────────────────────────
//  Installation Billing service
// ─────────────────────────────────────────────
export const billingService = {
  getPzlPriceList: async () => {
    const response = await api.get('/billing/pzl-price-list');
    return response.data;
  },

  updatePzlPriceList: async (data) => {
    const response = await api.put('/billing/pzl-price-list', data);
    return response.data;
  },

  updateInstallationPzlPricing: async (installationId, data) => {
    const response = await api.put(
      `/billing/installations/${installationId}/pzl-pricing`,
      data
    );
    return response.data;
  },

  updateSavedSettlementPzlPriceList: async (settlementId, data) => {
    const response = await api.put(
      `/billing/settlements/${settlementId}/pzl-price-list`,
      data
    );
    return response.data;
  },

  updateSavedSettlementInstallationPzlPricing: async (settlementId, installationId, data) => {
    const response = await api.put(
      `/billing/settlements/${settlementId}/installations/${installationId}/pzl-pricing`,
      data
    );
    return response.data;
  },

  updateSavedSettlementInstallationCalculation: async (settlementId, installationId, data) => {
    const response = await api.put(
      `/billing/settlements/${settlementId}/installations/${installationId}/calculation`,
      data
    );
    return response.data;
  },

  updateSavedSettlementTeamAdditionalAmount: async (settlementId, installationId, data) => {
    const response = await api.put(
      `/billing/settlements/${settlementId}/installations/${installationId}/team-additional-amount`,
      data
    );
    return response.data;
  },

  // Position rates
  listPositionRates: async () => {
    const response = await api.get('/billing/position-rates');
    return response.data;
  },

  createPositionRate: async (data) => {
    const response = await api.post('/billing/position-rates', data);
    return response.data;
  },

  updatePositionRate: async (id, data) => {
    const response = await api.put(`/billing/position-rates/${id}`, data);
    return response.data;
  },

  deletePositionRate: async (id) => {
    await api.delete(`/billing/position-rates/${id}`);
  },

  // Saved settlement snapshots
  listSavedSettlements: async () => {
    const response = await api.get('/billing/settlements');
    return response.data;
  },

  getSavedSettlement: async (id) => {
    const response = await api.get(`/billing/settlements/${id}`);
    return response.data;
  },

  createSavedSettlement: async (data) => {
    const response = await api.post('/billing/settlements', data);
    return response.data;
  },

  deleteSavedSettlement: async (id) => {
    await api.delete(`/billing/settlements/${id}`);
  },

  addInstallationUsl: async (installationId, data) => {
    const response = await api.post(`/billing/installations/${installationId}/usl`, data);
    return response.data;
  },

  listPeriodInstallations: async (startDate, endDate, settlementId) => {
    const response = await api.get('/billing/period-installations', {
      params: {
        start_date: startDate,
        end_date: endDate,
        settlement_id: settlementId,
      },
    });
    return response.data;
  },

  updateInstallationUsl: async (installationId, itemId, data) => {
    const response = await api.put(
      `/billing/installations/${installationId}/usl/${itemId}`,
      data
    );
    return response.data;
  },

  addSavedSettlementUsl: async (settlementId, installationId, data) => {
    const response = await api.post(
      `/billing/settlements/${settlementId}/installations/${installationId}/usl`,
      data
    );
    return response.data;
  },

  updateSavedSettlementUsl: async (settlementId, installationId, itemId, data) => {
    const response = await api.put(
      `/billing/settlements/${settlementId}/installations/${installationId}/usl/${itemId}`,
      data
    );
    return response.data;
  },

  // Earnings
  getEarnings: async ({ installerId, installationId, startDate, endDate } = {}) => {
    const response = await api.get('/billing/earnings', {
      params: {
        installer_id: installerId,
        installation_id: installationId,
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  },

  exportPdf: async (installerId, startDate, endDate) => {
    const response = await api.get('/billing/earnings/export/pdf', {
      params: { installer_id: installerId, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    let filename = 'rozliczenie.pdf';
    if (disposition) {
      const match = disposition.match(/filename="([^"]+)"/);
      if (match) filename = match[1];
    }
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportSavedSettlementPdf: async (settlementId, installerId) => {
    const response = await api.get(`/billing/settlements/${settlementId}/export/pdf`, {
      params: { installer_id: installerId },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename="([^"]+)"/);
    link.setAttribute('download', match?.[1] || 'rozliczenie.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportSavedSettlement: async (settlementId, format) => {
    if (!['pdf', 'xlsx', 'xlsx-kadra'].includes(format)) {
      throw new Error('Nieobsługiwany format eksportu');
    }
    await downloadFile(`/billing/settlements/${settlementId}/export/${format}`);
  },
};

export default api;

const taskRequest = (data, files = []) => {
  if (!files.length) return [data, {}];
  const form = new FormData();
  form.append('data', JSON.stringify(data));
  files.forEach(file => form.append('files', file));
  return [form, { headers: { 'Content-Type': 'multipart/form-data' } }];
};

export const tasksService = {
  organizations: async () => (await api.get('/tasks/organizations')).data,
  createOrganization: async (data) => (await api.post('/tasks/organizations', data)).data,
  deleteOrganization: async (id) => api.delete(`/tasks/organizations/${id}`),
  candidates: async (id) => (await api.get(`/tasks/organizations/${id}/candidates`)).data,
  updateMembers: async (id, member_ids) => (await api.put(`/tasks/organizations/${id}/members`, { member_ids })).data,
  tasks: async (id, params = {}) => (await api.get(`/tasks/organizations/${id}/tasks`, { params })).data,
  task: async (id, taskId) => (await api.get(`/tasks/organizations/${id}/tasks/${taskId}`)).data,
  comments: async (id, taskId, params = {}) => (await api.get(`/tasks/organizations/${id}/tasks/${taskId}/comments`, { params })).data,
  addComment: async (id, taskId, content, files = []) => (await api.post(`/tasks/organizations/${id}/tasks/${taskId}/comments`, ...taskRequest({ content }, files))).data,
  attachment: async (id, taskId, attachmentId, preview = false) => (await api.get(`/tasks/organizations/${id}/tasks/${taskId}/attachments/${attachmentId}`, { params: { preview }, responseType: 'blob' })).data,
  deleteComment: async (id, taskId, commentId) => api.delete(`/tasks/organizations/${id}/tasks/${taskId}/comments/${commentId}`),
  audit: async (id, params = {}) => (await api.get(`/tasks/organizations/${id}/audit`, { params })).data,
  createTask: async (id, data, files = []) => (await api.post(`/tasks/organizations/${id}/tasks`, ...taskRequest(data, files))).data,
  updateTask: async (id, taskId, data, files = []) => (await api.put(`/tasks/organizations/${id}/tasks/${taskId}`, ...taskRequest(data, files))).data,
  // Single-field changes: no full-task overwrite, so they never clash with other people's edits.
  updateTaskStatus: async (id, taskId, status) => (await api.patch(`/tasks/organizations/${id}/tasks/${taskId}/status`, { status })).data,
  updateTaskAssignees: async (id, taskId, delta) => (await api.patch(`/tasks/organizations/${id}/tasks/${taskId}/assignees`, delta)).data,
  updateChecklistItem: async (id, taskId, itemId, is_completed) => (await api.patch(`/tasks/organizations/${id}/tasks/${taskId}/checklist/${itemId}`, { is_completed })).data,
  setArchived: async (id, taskId, archived) => (await api.put(`/tasks/organizations/${id}/tasks/${taskId}/archive`, { archived })).data,
  setWatch: async (id, taskId, enabled) => (await api.put(`/tasks/organizations/${id}/tasks/${taskId}/watch`, { enabled })).data,
  deleteTask: async (id, taskId) => api.delete(`/tasks/organizations/${id}/tasks/${taskId}`),
};
